import { SearchHistoryItem } from './searchHistory';

// API Configuration
const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://dashboard.spyglasstrends.com' 
  : 'http://localhost:5173';

const WS_URL = process.env.NODE_ENV === 'production'
  ? 'wss://dashboard.spyglasstrends.com'
  : 'ws://localhost:5173';

// WebSocket connection
let ws: WebSocket | null = null;
let reconnectAttempts = 0;
const maxReconnectAttempts = 5;
const reconnectDelay = 1000;

// Event listeners for real-time updates
const eventListeners = new Set<(event: SearchHistoryUpdateEvent) => void>();

interface SearchHistoryUpdateEvent {
  type: 'SEARCH_ADDED' | 'SEARCH_UPDATED' | 'SEARCH_DELETED';
  data: any;
  userId?: string;
}

// Authentication token getter
const getAuthToken = (): string | null => {
  return localStorage.getItem('auth_token') || null;
};

// HTTP request helper with authentication
const apiRequest = async (endpoint: string, options: RequestInit = {}) => {
  const token = getAuthToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...options.headers,
  };

  const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status}: ${response.statusText}`);
  }

  return response.json();
};

// WebSocket connection management
const connectWebSocket = () => {
  if (ws && ws.readyState === WebSocket.OPEN) {
    return;
  }

  try {
    const token = getAuthToken();
    const wsUrl = `${WS_URL}/api/websocket${token ? `?token=${token}` : ''}`;
    ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('WebSocket connected');
      reconnectAttempts = 0;
      
      // Subscribe to search history updates
      ws?.send(JSON.stringify({
        type: 'SUBSCRIBE_SEARCH_HISTORY'
      }));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        handleWebSocketMessage(data);
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      ws = null;
      
      // Attempt to reconnect
      if (reconnectAttempts < maxReconnectAttempts) {
        reconnectAttempts++;
        setTimeout(() => {
          console.log(`Attempting to reconnect WebSocket (${reconnectAttempts}/${maxReconnectAttempts})`);
          connectWebSocket();
        }, reconnectDelay * reconnectAttempts);
      }
    };

    ws.onerror = (error) => {
      console.error('WebSocket error:', error);
    };
  } catch (error) {
    console.error('Failed to connect WebSocket:', error);
  }
};

// Handle WebSocket messages
const handleWebSocketMessage = (data: any) => {
  switch (data.type) {
    case 'SEARCH_HISTORY_UPDATE':
      // Notify all listeners about the update
      eventListeners.forEach(listener => {
        listener(data.data);
      });
      break;
    case 'CONNECTED':
      console.log('WebSocket connected successfully:', data.message);
      break;
    case 'SUBSCRIBED':
      console.log('Subscribed to:', data.subscription);
      break;
    default:
      // Handle other message types as needed
      break;
  }
};

// Initialize WebSocket connection
export const initializeRealTimeUpdates = () => {
  connectWebSocket();
};

// Add event listener for real-time updates
export const addSearchHistoryListener = (listener: (event: SearchHistoryUpdateEvent) => void) => {
  eventListeners.add(listener);
  
  // Return cleanup function
  return () => {
    eventListeners.delete(listener);
  };
};

// API Methods
export const searchHistoryApi = {
  // Load search history from server
  async loadSearchHistory(): Promise<SearchHistoryItem[]> {
    try {
      const response = await apiRequest('/search-history');
      return response.data.map(transformServerDataToClient);
    } catch (error) {
      console.error('Failed to load search history from server:', error);
      
      // Fallback to localStorage
      return loadSearchHistoryFromLocalStorage();
    }
  },

  // Add new search to server
  async addSearchResult(taskId: string, productName: string, objective: string): Promise<void> {
    try {
      await apiRequest('/search-history', {
        method: 'POST',
        body: JSON.stringify({
          taskId,
          productName,
          objective,
          status: 'processing'
        })
      });
    } catch (error) {
      console.error('Failed to add search result to server:', error);
      
      // Fallback to localStorage
      addSearchResultToLocalStorage(taskId, productName, objective);
    }
  },

  // Update search result on server
  async updateSearchResult(
    taskId: string, 
    status: 'completed' | 'failed',
    analysisData?: any
  ): Promise<void> {
    try {
      await apiRequest('/search-history', {
        method: 'PUT',
        body: JSON.stringify({
          taskId,
          status,
          analysisData
        })
      });
    } catch (error) {
      console.error('Failed to update search result on server:', error);
      
      // Fallback to localStorage
      updateSearchResultInLocalStorage(taskId, status, analysisData);
    }
  },

  // Delete search result from server
  async deleteSearchResult(taskId: string): Promise<void> {
    try {
      await apiRequest(`/search-history?taskId=${taskId}`, {
        method: 'DELETE'
      });
    } catch (error) {
      console.error('Failed to delete search result from server:', error);
      
      // Fallback to localStorage
      deleteSearchResultFromLocalStorage(taskId);
    }
  },

  // Clear all search history
  async clearSearchHistory(): Promise<void> {
    try {
      // For bulk delete, you might want to add a specific endpoint
      const history = await this.loadSearchHistory();
      await Promise.all(
        history.map(item => this.deleteSearchResult(item.id))
      );
    } catch (error) {
      console.error('Failed to clear search history on server:', error);
      
      // Fallback to localStorage
      clearSearchHistoryFromLocalStorage();
    }
  },

  // Get a specific search result by ID
  async getSearchResult(taskId: string): Promise<SearchHistoryItem | null> {
    if (!taskId) return null;
    
    try {
      const history = await this.loadSearchHistory();
      return history.find(item => item.id === taskId) || null;
    } catch (error) {
      console.error('Failed to get search result from server:', error);
      
      // Fallback to localStorage
      const history = loadSearchHistoryFromLocalStorage();
      return history.find(item => item.id === taskId) || null;
    }
  }
};

// Transform server data to client format
const transformServerDataToClient = (serverData: any): SearchHistoryItem => {
  return {
    id: serverData.id,
    productName: serverData.product_name,
    objective: serverData.objective,
    timestamp: serverData.timestamp,
    status: serverData.status,
    costReduction: serverData.cost_reduction,
    marketValue: serverData.market_value,
    consumerInterest: serverData.consumer_interest,
    sustainabilityScore: serverData.sustainability_score,
    formulations: serverData.formulations,
    evaluations: serverData.evaluations,
    recommendations: serverData.recommendations,
    marketInformation: serverData.market_information,
    supplyChainData: serverData.supply_chain_data
  };
};

// LocalStorage fallback functions (same as original)
const STORAGE_KEY = 'spyglass_search_history';
const MAX_HISTORY_ITEMS = 100;

const loadSearchHistoryFromLocalStorage = (): SearchHistoryItem[] => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return parsed.filter(item => item && item.id && item.productName);
      }
    }
  } catch (error) {
    localStorage.removeItem(STORAGE_KEY);
  }
  return [];
};

const saveSearchHistoryToLocalStorage = (history: SearchHistoryItem[]) => {
  try {
    const limitedHistory = history.slice(0, MAX_HISTORY_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(limitedHistory));
  } catch (error) {
    console.error('Failed to save to localStorage:', error);
  }
};

const addSearchResultToLocalStorage = (taskId: string, productName: string, objective: string) => {
  const history = loadSearchHistoryFromLocalStorage();
  const newItem: SearchHistoryItem = {
    id: taskId,
    productName: productName.trim(),
    objective: objective.trim(),
    timestamp: new Date().toISOString(),
    status: 'processing'
  };
  
  const existingIndex = history.findIndex(item => item.id === taskId);
  if (existingIndex >= 0) {
    history[existingIndex] = { ...history[existingIndex], ...newItem };
  } else {
    history.unshift(newItem);
  }
  
  saveSearchHistoryToLocalStorage(history);
};

const updateSearchResultInLocalStorage = (taskId: string, status: 'completed' | 'failed', analysisData?: any) => {
  const history = loadSearchHistoryFromLocalStorage();
  const itemIndex = history.findIndex(item => item.id === taskId);
  
  if (itemIndex >= 0) {
    history[itemIndex] = {
      ...history[itemIndex],
      status,
      ...(analysisData && analysisData)
    };
    saveSearchHistoryToLocalStorage(history);
  }
};

const deleteSearchResultFromLocalStorage = (taskId: string) => {
  const history = loadSearchHistoryFromLocalStorage();
  const filteredHistory = history.filter(item => item.id !== taskId);
  saveSearchHistoryToLocalStorage(filteredHistory);
};

const clearSearchHistoryFromLocalStorage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear localStorage:', error);
  }
};

// Sync localStorage with server (for offline-to-online sync)
export const syncLocalStorageWithServer = async () => {
  try {
    const localHistory = loadSearchHistoryFromLocalStorage();
    const serverHistory = await searchHistoryApi.loadSearchHistory();
    
    // Find items that exist locally but not on server
    const localOnly = localHistory.filter(
      localItem => !serverHistory.find(serverItem => serverItem.id === localItem.id)
    );
    
    // Sync local-only items to server
    for (const item of localOnly) {
      try {
        await searchHistoryApi.addSearchResult(item.id, item.productName, item.objective);
        if (item.status !== 'processing') {
          await searchHistoryApi.updateSearchResult(item.id, item.status, {
            costReduction: item.costReduction,
            marketValue: item.marketValue,
            consumerInterest: item.consumerInterest,
            sustainabilityScore: item.sustainabilityScore,
            formulations: item.formulations,
            evaluations: item.evaluations,
            recommendations: item.recommendations,
            marketInformation: item.marketInformation,
            supplyChainData: item.supplyChainData
          });
        }
      } catch (error) {
        console.error('Failed to sync item to server:', item.id, error);
      }
    }
  } catch (error) {
    console.error('Failed to sync with server:', error);
  }
}; 